import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { HTime } from '@shared/src/datatypes/HTime';
import { DocumentDetailDto } from '@shared/src/dtos/document/DocumentDetailDto';
import { DocumentDto } from '@shared/src/dtos/document/DocumentDto';
import { ImageDocument } from '@shared/src/dtos/document/ImageDocument';
import { IncidentDto } from '@shared/src/dtos/incident/IncidentDto';
import { IncidentGroupStructDto } from '@shared/src/dtos/incident/IncidentGroupStructDto';
import { IncidentsStructDto } from '@shared/src/dtos/incident/IncidentsStructDto';
import { IncidentStructDto } from '@shared/src/dtos/incident/IncidentStructDto';
import { IncidentGroupTypes } from '@shared/src/enums/IncidentGroupTypes';
import { IncidentTypes } from '@shared/src/enums/IncidentTypes';
import { RouteDocumentTypes } from '@shared/src/enums/RouteDocumentTypes';
import { Observable } from 'rxjs';

/**
 * Funcionalitat comuna per informar d'una incidència (problema): grup, tipus, comentari, 'estic parat' amb el temps
 * previst i fotos. L'app (endpoints privats) i les pantalles externes (endpoints oberts) en deriven; cadascuna amb
 * els seus estils.
 */
@Component({
    template: ''
})
export abstract class SharedIncidentAbstractComponent implements OnInit {

    public readonly = false;
    public readonlyReport = false;

    public incidentsStruct: IncidentsStructDto;

    public comment: string;
    public standStill: boolean;
    public time: HTime = HTime.buildByHoursAndMinutes(1, 0);
    public mintime = HTime.buildByHoursAndMinutes(0, 30);

    public incidentGroupSelected: IncidentGroupStructDto;
    public incidentSelected: IncidentStructDto;

    public imageList: ImageDocument[];

    @Output() public refreshIncident: EventEmitter<boolean> = new EventEmitter();

    /** Estructura de grups i tipus d'incidència. Si encara no es pot carregar, retorna null */
    protected abstract getIncidentStruct(): Observable<IncidentsStructDto>;

    /** Envia la incidència al backend */
    protected abstract sendIncident(incident: IncidentDto): Observable<boolean>;

    protected abstract getRouteId(): number;
    protected abstract getPhaseId(): number;
    protected abstract getShipmentId(): number;

    /** Es pot indicar que s'està parat */
    public canStandStill(): boolean {
        return true;
    }

    /** Un cop reportada la incidència correctament */
    protected onIncidentReported(): void {
    }

    public ngOnInit(): void {
        this.loadIncidentStruct();
    }

    public loadIncidentStruct(): void {
        const request = this.getIncidentStruct();
        if (request == null)
            return;
        request.subscribe(data => {
            this.incidentsStruct = data;
        });
    }

    public onIncidentGroupClick(incidentGroup: IncidentGroupStructDto): void {
        if (this.isGroupSelected(incidentGroup))
            this.incidentGroupSelected = null;
        else
            this.incidentGroupSelected = incidentGroup;
        this.incidentSelected = null;
    }

    public onIncidentClick(incident: IncidentStructDto): void {
        if (this.isIncidentSelected(incident))
            this.incidentSelected = null;
        else
            this.incidentSelected = incident;
    }

    public isGroupSelected(incidentGroup: IncidentGroupStructDto): boolean {
        return this.incidentGroupSelected != null && incidentGroup != null
            && this.incidentGroupSelected.incidentGroup === incidentGroup.incidentGroup;
    }

    public isIncidentSelected(incident: IncidentStructDto): boolean {
        return this.incidentSelected != null && incident != null && this.incidentSelected.incident === incident.incident;
    }

    /** Grups a mostrar: tots, o només el seleccionat */
    public getVisibleGroups(): IncidentGroupStructDto[] {
        if (this.incidentsStruct == null || this.incidentsStruct.incidents == null)
            return [];
        if (this.incidentGroupSelected != null)
            return this.incidentsStruct.incidents.filter(g => this.isGroupSelected(g));
        return this.incidentsStruct.incidents;
    }

    /** Tipus d'incidència del grup seleccionat a mostrar: tots, o només el seleccionat */
    public getVisibleIncidents(): IncidentStructDto[] {
        if (this.incidentGroupSelected == null || this.incidentGroupSelected.incidents == null)
            return [];
        if (this.incidentSelected != null)
            return this.incidentGroupSelected.incidents.filter(i => this.isIncidentSelected(i));
        return this.incidentGroupSelected.incidents;
    }

    public changeComment(value: string): void {
    }

    public onStandStill(): void {
        this.standStill = !this.standStill;
    }

    public getShowOtherData(): boolean {
        return this.incidentGroupSelected != null && (this.incidentSelected != null
            || this.incidentGroupSelected.incidents == null || this.incidentGroupSelected.incidents.length === 0);
    }

    public getIncident(): IncidentDto {
        const incidentGroup: IncidentGroupTypes = this.incidentGroupSelected != null ? this.incidentGroupSelected.incidentGroup
            : IncidentGroupTypes.None;
        const incident: IncidentTypes = this.incidentSelected != null ? this.incidentSelected.incident : IncidentTypes.None;
        const standStill = this.canStandStill() && (this.standStill || incident === IncidentTypes.EstoyParado);
        return IncidentDto.build(this.getRouteId(), this.getPhaseId(), this.getShipmentId(), incidentGroup, incident,
            this.comment, standStill, incident === IncidentTypes.HePinchado, this.time);
    }

    public isValid(): boolean {
        return this.getIncident().isValid();
    }

    public getPrefixFileNameImage(): string {
        return 'Incident.';
    }

    public onSelectedImage(value: ImageDocument): void {
    }

    public onTakeImage(images: ImageDocument[]): void {
        this.imageList = images;
    }

    public onReport(): void {
        if (this.readonlyReport)
            return;

        const incident = this.getIncident();
        if (!incident.isValid())
            return;

        incident.documents = new Array();
        if (this.imageList != null) {
            this.imageList.forEach(element => {
                const documentList: DocumentDto = new DocumentDto();
                const document: DocumentDetailDto = new DocumentDetailDto();
                document.documentType = RouteDocumentTypes.TripIncident;
                document.fileName = element.filename;
                document.fileBytes = element.filebytes;
                document.fileType = element.filetypes;
                documentList.documentDetailList.push(document);
                incident.documents.push(documentList);
            });
        }

        this.readonlyReport = true;
        this.sendIncident(incident).subscribe(data => {
            this.readonlyReport = this.readonly;
            if (data) {
                this.refreshIncident.next(true);
                this.onIncidentReported();
            }
        }, () => {
            this.readonlyReport = this.readonly;
        });
    }
}
